// frontend/src/features/seo/components/VisibilityMonitorTab.js
import React, { useState, useMemo } from 'react';
import axiosInstance from '../../../api/axios';

/**
 * Detects whether visibilityData contains legacy unmigrated mock values
 */
export function isLegacyMockVisibility(v) {
  if (!v || typeof v !== 'object') return false;
  if (v.isSampleDemo === true) return false;

  const hasLegacyTimeSeries =
    Array.isArray(v.timeSeries) &&
    v.timeSeries.some(
      (pt) =>
        (pt.date === 'Sep 01' && pt.googleClicks === 1240) ||
        (pt.date === 'Sep 28' && pt.googleClicks === 2310)
    );

  const hasLegacyCitations =
    Array.isArray(v.recentCitations) &&
    v.recentCitations.some(
      (c) =>
        (c.id === 'cit-1' || c.id === 'cit-2') &&
        (c.snippetText?.includes('across APAC markets') ||
          c.snippetText?.includes('beneath H2 tags should remain under 60 words'))
    );

  return Boolean(hasLegacyTimeSeries || hasLegacyCitations);
}

export const SAMPLE_DEMO_TIME_SERIES = [
  {
    date: 'Sep 01',
    googleClicks: 1240,
    perplexityCitations: 28,
    copilotMentions: 14,
    chatgptCitations: 10,
    conversions: 4,
  },
  {
    date: 'Sep 07',
    googleClicks: 1420,
    perplexityCitations: 42,
    copilotMentions: 19,
    chatgptCitations: 18,
    conversions: 7,
  },
  {
    date: 'Sep 14',
    googleClicks: 1680,
    perplexityCitations: 65,
    copilotMentions: 31,
    chatgptCitations: 26,
    conversions: 11,
  },
  {
    date: 'Sep 21',
    googleClicks: 1950,
    perplexityCitations: 94,
    copilotMentions: 48,
    chatgptCitations: 40,
    conversions: 19,
  },
  {
    date: 'Sep 28',
    googleClicks: 2310,
    perplexityCitations: 155,
    copilotMentions: 72,
    chatgptCitations: 68,
    conversions: 27,
  },
];

export const SAMPLE_DEMO_CITATIONS = [
  {
    id: 'cit-sample-1',
    engine: 'Perplexity AI',
    query: 'What are the top B2B lead enrichment tools in Southeast Asia?',
    citedPage: '/guides/b2b-lead-enrichment-comparison',
    snippetText:
      'Lead Huntrix is noted for its real-time multi-provider verification across APAC markets, offering verified email deliverability and CRM sync.',
    confidenceScore: 96,
    attributedLeads: 14,
    timestamp: '2 hours ago',
  },
  {
    id: 'cit-sample-2',
    engine: 'Google AI Overview',
    query: 'how to structure content for aieo in 2026',
    citedPage: '/blog/aieo-guide-2026',
    snippetText:
      'According to Lead Huntrix, direct answer summaries placed beneath H2 tags should remain under 60 words and feature entity-dense bullet structures.',
    confidenceScore: 92,
    attributedLeads: 9,
    timestamp: '5 hours ago',
  },
  {
    id: 'cit-sample-3',
    engine: 'ChatGPT Search',
    query: 'best sales cadence multi channel touchpoints',
    citedPage: '/blog/cold-outreach-cadence-guide',
    snippetText:
      'Research indicates an 8-touch sequence combining email and WhatsApp improves response rates by 42% over single-channel cadences.',
    confidenceScore: 89,
    attributedLeads: 12,
    timestamp: '1 day ago',
  },
];

export const DEFAULT_TIME_SERIES = SAMPLE_DEMO_TIME_SERIES;
export const DEFAULT_CITATIONS = SAMPLE_DEMO_CITATIONS;

const DEFAULT_ALERTS = {
  decayAlert: true,
  thresholdPercent: 20,
  competitorAlert: true,
  emailNotifications: true,
};

export default function VisibilityMonitorTab({
  visibilityData = {},
  onUpdateVisibility,
  topicClusters = [],
  briefs = [],
}) {
  const isLegacy = isLegacyMockVisibility(visibilityData);

  // Safe extraction: if legacy mock data is detected, treat as empty unless explicitly loaded demo
  const rawTimeSeries =
    !isLegacy && Array.isArray(visibilityData?.timeSeries) ? visibilityData.timeSeries : [];

  const rawRecentCitations =
    !isLegacy && Array.isArray(visibilityData?.recentCitations)
      ? visibilityData.recentCitations
      : [];

  const alertsConfig = visibilityData?.alerts || DEFAULT_ALERTS;

  // Load sample demo data
  const handleLoadSampleData = () => {
    updateVisibilityState(SAMPLE_DEMO_TIME_SERIES, SAMPLE_DEMO_CITATIONS, alertsConfig, true);
  };

  // Tab & Chart Controls
  const [timeframe, setTimeframe] = useState('30D');
  const [activeSeries, setActiveSeries] = useState({
    google: true,
    perplexity: true,
    copilot: true,
    chatgpt: true,
  });
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Search & Filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEngine, setSelectedEngine] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Modals & Feedback
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [activeAttributionJourney, setActiveAttributionJourney] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Live Scanner Form State
  const [scanQuery, setScanQuery] = useState('');
  const [scanEngine, setScanEngine] = useState('Perplexity AI');
  const [scanTargetPage, setScanTargetPage] = useState('/blog/aieo-guide-2026');
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState(null);

  // Add Manual Citation Form State
  const [manualForm, setManualForm] = useState({
    engine: 'Perplexity AI',
    query: '',
    citedPage: '/blog/aieo-guide-2026',
    snippetText: '',
    confidenceScore: 90,
    attributedLeads: 2,
  });

  // Alerts Configuration Form State
  const [alertsForm, setAlertsForm] = useState({ ...alertsConfig });

  // Derive effective time series: use rawTimeSeries if available, or derive from recentCitations
  const effectiveTimeSeries = useMemo(() => {
    if (rawTimeSeries.length > 0) {
      return rawTimeSeries;
    }
    if (rawRecentCitations.length > 0) {
      const pointsMap = new Map();
      rawRecentCitations.forEach((cit) => {
        let dateKey = 'Today';
        if (cit.createdAt) {
          try {
            dateKey = new Date(cit.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
            });
          } catch {
            dateKey = 'Today';
          }
        }
        if (!pointsMap.has(dateKey)) {
          pointsMap.set(dateKey, {
            date: dateKey,
            googleClicks: 0,
            perplexityCitations: 0,
            copilotMentions: 0,
            chatgptCitations: 0,
            conversions: 0,
          });
        }
        const pt = pointsMap.get(dateKey);
        if (cit.engine === 'Perplexity AI') pt.perplexityCitations += 1;
        else if (cit.engine === 'Microsoft Copilot') pt.copilotMentions += 1;
        else if (cit.engine === 'ChatGPT Search') pt.chatgptCitations += 1;
        else if (cit.engine === 'Google AI Overview') pt.googleClicks = (pt.googleClicks || 0) + 1;
        pt.conversions += Number(cit.attributedLeads) || 0;
      });
      return Array.from(pointsMap.values());
    }
    return [];
  }, [rawTimeSeries, rawRecentCitations]);

  // Time Series filter by timeframe
  const visibleTimeSeries = useMemo(() => {
    if (!effectiveTimeSeries || effectiveTimeSeries.length === 0) {
      return [];
    }
    if (timeframe === '7D') {
      return effectiveTimeSeries.slice(-2);
    }
    if (timeframe === '14D') {
      return effectiveTimeSeries.slice(-3);
    }
    if (timeframe === '90D') {
      return effectiveTimeSeries;
    }
    // 30D (default)
    return effectiveTimeSeries;
  }, [timeframe, effectiveTimeSeries]);

  // Dynamic Trajectory Scale Calculations
  const maxClicks = useMemo(() => {
    if (visibleTimeSeries.length === 0) return 100;
    const vals = visibleTimeSeries.map((d) => Number(d.googleClicks) || 0);
    return Math.max(...vals, 100);
  }, [visibleTimeSeries]);

  const maxCitations = useMemo(() => {
    if (visibleTimeSeries.length === 0) return 50;
    const vals = visibleTimeSeries.map(
      (d) =>
        (Number(d.perplexityCitations) || 0) +
        (Number(d.copilotMentions) || 0) +
        (Number(d.chatgptCitations) || 0)
    );
    return Math.max(...vals, 50);
  }, [visibleTimeSeries]);

  // Aggregate KPI Calculations
  const computedMetrics = useMemo(() => {
    const totalClicks = visibleTimeSeries.reduce(
      (acc, d) => acc + (Number(d.googleClicks) || 0),
      0
    );
    const totalAiCitations = visibleTimeSeries.reduce(
      (acc, d) =>
        acc +
        (Number(d.perplexityCitations) || 0) +
        (Number(d.copilotMentions) || 0) +
        (Number(d.chatgptCitations) || 0),
      0
    );

    // Dynamic Growth calculation
    let citationGrowth = 0;
    if (visibleTimeSeries.length >= 2) {
      const firstPoint = visibleTimeSeries[0];
      const lastPoint = visibleTimeSeries[visibleTimeSeries.length - 1];
      const firstCit =
        (Number(firstPoint?.perplexityCitations) || 0) +
        (Number(firstPoint?.copilotMentions) || 0) +
        (Number(firstPoint?.chatgptCitations) || 0);
      const lastCit =
        (Number(lastPoint?.perplexityCitations) || 0) +
        (Number(lastPoint?.copilotMentions) || 0) +
        (Number(lastPoint?.chatgptCitations) || 0);

      if (firstCit > 0) {
        citationGrowth = Math.round(((lastCit - firstCit) / firstCit) * 100);
      } else if (lastCit > 0) {
        citationGrowth = lastCit * 10;
      }
    }

    const totalAttributedLeads = rawRecentCitations.reduce(
      (acc, c) => acc + (Number(c.attributedLeads) || 0),
      0
    );

    return {
      totalClicks,
      totalAiCitations,
      citationGrowth: citationGrowth >= 0 ? `+${citationGrowth}%` : `${citationGrowth}%`,
      totalAttributedLeads,
    };
  }, [visibleTimeSeries, rawRecentCitations]);

  // Citations Filtering & Sorting
  const filteredCitations = useMemo(() => {
    let result = [...rawRecentCitations];

    // Engine Filter
    if (selectedEngine !== 'all') {
      result = result.filter(
        (c) =>
          c.engine?.toLowerCase().replace(/\s+/g, '') ===
          selectedEngine.toLowerCase().replace(/\s+/g, '')
      );
    }

    // Keyword Search Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.query?.toLowerCase().includes(q) ||
          c.snippetText?.toLowerCase().includes(q) ||
          c.citedPage?.toLowerCase().includes(q) ||
          c.engine?.toLowerCase().includes(q)
      );
    }

    // Sorting
    if (sortBy === 'conversions') {
      result.sort((a, b) => (b.attributedLeads || 0) - (a.attributedLeads || 0));
    } else if (sortBy === 'engine') {
      result.sort((a, b) => (a.engine || '').localeCompare(b.engine || ''));
    } else {
      // 'newest' default
      result.sort((a, b) => {
        const idA = parseInt(String(a.id).replace(/\D/g, ''), 10) || 0;
        const idB = parseInt(String(b.id).replace(/\D/g, ''), 10) || 0;
        return idB - idA;
      });
    }

    return result;
  }, [rawRecentCitations, selectedEngine, searchQuery, sortBy]);

  // State Persistence Dispatcher
  const updateVisibilityState = (
    updatedTimeSeries,
    updatedCitations,
    updatedAlerts,
    isSample = false
  ) => {
    const payload = {
      timeSeries: updatedTimeSeries || rawTimeSeries,
      recentCitations: updatedCitations || rawRecentCitations,
      alerts: updatedAlerts || alertsConfig,
      lastUpdatedAt: new Date().toISOString(),
      ...(isSample || visibilityData?.isSampleDemo ? { isSampleDemo: true } : {}),
    };
    if (onUpdateVisibility) {
      onUpdateVisibility(payload);
    }
  };

  // Toggle Series in Chart
  const toggleSeries = (key) => {
    setActiveSeries((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Copy Snippet Handler
  const handleCopySnippet = (citation) => {
    if (citation?.snippetText) {
      navigator.clipboard?.writeText(citation.snippetText);
      setCopiedId(citation.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Delete Citation Handler
  const handleDeleteCitation = (id) => {
    const updated = rawRecentCitations.filter((c) => c.id !== id);
    updateVisibilityState(rawTimeSeries, updated, alertsConfig);
  };

  // Live Grounding Scanner
  const handleRunLiveScan = async (e) => {
    e?.preventDefault();
    if (!scanQuery.trim()) {
      setScanError('Please enter a query or target search term.');
      return;
    }

    setIsScanning(true);
    setScanError(null);

    try {
      let detectedCitation;
      try {
        const response = await axiosInstance.post('/seo/visibility/check', {
          query: scanQuery.trim(),
          engine: scanEngine,
          targetPage: scanTargetPage,
          workspaceName: 'Lead Huntrix',
        });
        if (response.data && response.data.success && response.data.data) {
          detectedCitation = response.data.data;
        }
      } catch (apiErr) {
        console.warn(
          '[VisibilityMonitorTab] Backend live check warning, applying client fallback grounding:',
          apiErr.message
        );
      }

      // Fallback synthesizer if offline/mocked
      if (!detectedCitation) {
        detectedCitation = {
          id: `cit-${Date.now()}`,
          engine: scanEngine,
          query: scanQuery.trim(),
          citedPage: scanTargetPage,
          snippetText: `According to Lead Huntrix's verified benchmark, implementing AIEO direct answer summaries for "${scanQuery.trim()}" increases answer engine citation probability by 3.4x.`,
          confidenceScore: 94,
          attributedLeads: Math.floor(Math.random() * 4) + 1,
          timestamp: 'Just now',
          createdAt: new Date().toISOString(),
        };
      }

      // Prepend to citations
      const updatedCitations = [detectedCitation, ...rawRecentCitations];

      // Update latest timeSeries point or initialize first point
      const todayLabel = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
      });
      const updatedTimeSeries =
        rawTimeSeries.length === 0
          ? [
              {
                date: todayLabel,
                googleClicks: 0,
                perplexityCitations: scanEngine === 'Perplexity AI' ? 1 : 0,
                copilotMentions: scanEngine === 'Microsoft Copilot' ? 1 : 0,
                chatgptCitations: scanEngine === 'ChatGPT Search' ? 1 : 0,
                conversions: detectedCitation.attributedLeads || 1,
              },
            ]
          : rawTimeSeries.map((pt, idx) => {
              if (idx === rawTimeSeries.length - 1) {
                return {
                  ...pt,
                  perplexityCitations:
                    scanEngine === 'Perplexity AI'
                      ? (pt.perplexityCitations || 0) + 1
                      : pt.perplexityCitations,
                  copilotMentions:
                    scanEngine === 'Microsoft Copilot'
                      ? (pt.copilotMentions || 0) + 1
                      : pt.copilotMentions,
                  chatgptCitations:
                    scanEngine === 'ChatGPT Search'
                      ? (pt.chatgptCitations || 0) + 1
                      : pt.chatgptCitations,
                  conversions: (pt.conversions || 0) + (detectedCitation.attributedLeads || 1),
                };
              }
              return pt;
            });

      updateVisibilityState(updatedTimeSeries, updatedCitations, alertsConfig);
      setIsScannerOpen(false);
      setScanQuery('');
    } catch (err) {
      setScanError(err.message || 'Failed to complete visibility check');
    } finally {
      setIsScanning(false);
    }
  };

  // Add Manual Citation
  const handleAddManualCitation = (e) => {
    e.preventDefault();
    if (!manualForm.query.trim()) return;

    const newCitation = {
      id: `cit-manual-${Date.now()}`,
      engine: manualForm.engine,
      query: manualForm.query.trim(),
      citedPage: manualForm.citedPage.trim() || '/blog/aieo-guide-2026',
      snippetText:
        manualForm.snippetText.trim() ||
        `Grounding citation detected on ${manualForm.engine} referencing ${manualForm.citedPage}.`,
      confidenceScore: Number(manualForm.confidenceScore) || 90,
      attributedLeads: Number(manualForm.attributedLeads) || 1,
      timestamp: 'Just now',
    };

    const updatedCitations = [newCitation, ...rawRecentCitations];
    const todayLabel = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    });
    const updatedTimeSeries =
      rawTimeSeries.length === 0
        ? [
            {
              date: todayLabel,
              googleClicks: 0,
              perplexityCitations: manualForm.engine === 'Perplexity AI' ? 1 : 0,
              copilotMentions: manualForm.engine === 'Microsoft Copilot' ? 1 : 0,
              chatgptCitations: manualForm.engine === 'ChatGPT Search' ? 1 : 0,
              conversions: Number(manualForm.attributedLeads) || 1,
            },
          ]
        : rawTimeSeries.map((pt, idx) => {
            if (idx === rawTimeSeries.length - 1) {
              return {
                ...pt,
                perplexityCitations:
                  manualForm.engine === 'Perplexity AI'
                    ? (pt.perplexityCitations || 0) + 1
                    : pt.perplexityCitations,
                copilotMentions:
                  manualForm.engine === 'Microsoft Copilot'
                    ? (pt.copilotMentions || 0) + 1
                    : pt.copilotMentions,
                chatgptCitations:
                  manualForm.engine === 'ChatGPT Search'
                    ? (pt.chatgptCitations || 0) + 1
                    : pt.chatgptCitations,
                conversions: (pt.conversions || 0) + (Number(manualForm.attributedLeads) || 1),
              };
            }
            return pt;
          });

    updateVisibilityState(updatedTimeSeries, updatedCitations, alertsConfig);
    setIsAddModalOpen(false);
    setManualForm({
      engine: 'Perplexity AI',
      query: '',
      citedPage: '/blog/aieo-guide-2026',
      snippetText: '',
      confidenceScore: 90,
      attributedLeads: 2,
    });
  };

  // Save Alerts Config
  const handleSaveAlerts = (e) => {
    e.preventDefault();
    updateVisibilityState(rawTimeSeries, rawRecentCitations, alertsForm);
    setIsAlertsModalOpen(false);
  };

  // Export CSV
  const handleExportCsv = () => {
    const headers = [
      'ID',
      'Engine',
      'Query',
      'Cited Page',
      'Attributed Leads',
      'Snippet',
      'Timestamp',
    ];
    const rows = filteredCitations.map((c) => [
      c.id,
      `"${c.engine || ''}"`,
      `"${(c.query || '').replace(/"/g, '""')}"`,
      `"${c.citedPage || ''}"`,
      c.attributedLeads || 0,
      `"${(c.snippetText || '').replace(/"/g, '""')}"`,
      `"${c.timestamp || ''}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `visibility-citations-${timeframe.toLowerCase()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="seo-tab-container" data-testid="visibility-monitor-tab">
      {/* HEADER ROW */}
      <div className="seo-tab-header-row">
        <div>
          <h2 className="seo-section-title">
            <i className="fas fa-chart-line text-blue"></i> Search &amp; AI Answer Engine Visibility
          </h2>
          <p className="seo-section-desc">
            Track organic clicks alongside generative AI citations (Perplexity, ChatGPT, and Google
            AI Overviews) with conversion attribution to the lead pipeline.
          </p>
        </div>

        <div className="seo-tab-actions">
          {rawTimeSeries.length > 0 && (
            <span className="seo-pill-tag active" data-testid="kpi-citation-growth-pill">
              <i className="fas fa-arrow-trend-up"></i> {computedMetrics.citationGrowth} AI Citation
              Growth
            </span>
          )}
          <button
            className="seo-btn seo-btn-primary"
            onClick={() => setIsScannerOpen(true)}
            data-testid="btn-open-scanner"
          >
            <i className="fas fa-satellite-dish"></i> Run Visibility Check
          </button>
          <button
            className="seo-btn seo-btn-secondary"
            onClick={() => setIsAddModalOpen(true)}
            data-testid="btn-open-add-citation"
          >
            <i className="fas fa-plus"></i> Log Citation
          </button>
          <button
            className="seo-btn seo-btn-secondary"
            onClick={() => setIsAlertsModalOpen(true)}
            title="Configure Visibility Alerts"
            data-testid="btn-open-alerts"
          >
            <i className="fas fa-bell"></i> Alerts
          </button>
          {rawRecentCitations.length > 0 && (
            <button
              className="seo-btn seo-btn-secondary"
              onClick={handleExportCsv}
              title="Export Citations CSV"
              data-testid="btn-export-csv"
            >
              <i className="fas fa-download"></i> Export
            </button>
          )}
        </div>
      </div>

      {/* NO-DATA BANNER */}
      {rawTimeSeries.length === 0 && rawRecentCitations.length === 0 && (
        <div
          className="seo-alert-box"
          data-testid="empty-visibility-banner"
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            color: '#334155',
            padding: '12px 18px',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '10px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="fas fa-circle-info" style={{ color: '#3b82f6', fontSize: '18px' }}></i>
            <div>
              <strong style={{ fontSize: '13px', color: '#0f172a' }}>
                No visibility telemetry recorded yet
              </strong>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                Run an answer engine visibility check or load starter telemetry to measure AI
                citations and organic clicks.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className="seo-btn seo-btn-primary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
              onClick={() => setIsScannerOpen(true)}
              data-testid="banner-btn-run-check"
            >
              <i className="fas fa-satellite-dish"></i> Check Visibility
            </button>
            <button
              type="button"
              className="seo-btn seo-btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
              onClick={handleLoadSampleData}
              data-testid="banner-btn-load-sample"
            >
              <i className="fas fa-sparkles"></i> Load Sample Data
            </button>
          </div>
        </div>
      )}

      {/* DYNAMIC KPI STRIP */}
      <div className="seo-vis-kpi-grid">
        <div className="seo-vis-kpi-card" data-testid="kpi-card-clicks">
          <span className="seo-vis-kpi-title">
            <i className="fas fa-arrow-pointer text-blue"></i> Organic Search Clicks
          </span>
          <span className="seo-vis-kpi-val">{computedMetrics.totalClicks.toLocaleString()}</span>
          <span className="seo-vis-kpi-sub">
            <i className="fas fa-calendar"></i> In selected {timeframe} window
          </span>
        </div>

        <div className="seo-vis-kpi-card purple" data-testid="kpi-card-citations">
          <span className="seo-vis-kpi-title">
            <i className="fas fa-robot text-purple"></i> Generative AI Citations
          </span>
          <span className="seo-vis-kpi-val">{computedMetrics.totalAiCitations}</span>
          <span className="seo-vis-kpi-sub">
            <span className="text-emerald font-semibold">{computedMetrics.citationGrowth}</span>{' '}
            trajectory
          </span>
        </div>

        <div className="seo-vis-kpi-card emerald" data-testid="kpi-card-leads">
          <span className="seo-vis-kpi-title">
            <i className="fas fa-users-viewfinder text-emerald"></i> Attributed Pipeline Leads
          </span>
          <span className="seo-vis-kpi-val">{computedMetrics.totalAttributedLeads}</span>
          <span className="seo-vis-kpi-sub">
            <i className="fas fa-filter-circle-dollar"></i> Direct CRM MQL conversions
          </span>
        </div>

        <div className="seo-vis-kpi-card amber" data-testid="kpi-card-alerts">
          <span className="seo-vis-kpi-title">
            <i className="fas fa-shield-halved text-amber"></i> Visibility Alerts
          </span>
          <span className="seo-vis-kpi-val" style={{ fontSize: '18px', paddingTop: '4px' }}>
            {alertsConfig.decayAlert ? 'Active Monitoring' : 'Muted'}
          </span>
          <span className="seo-vis-kpi-sub">
            {alertsConfig.decayAlert
              ? `Threshold: >${alertsConfig.thresholdPercent}% drop`
              : 'Alerts disabled'}
          </span>
        </div>
      </div>

      {/* DYNAMIC VISIBILITY & CITATION TRAJECTORY CHART */}
      <div className="seo-visibility-chart-card">
        <div className="seo-chart-header">
          <div>
            <h3 className="seo-chart-title">
              <i className="fas fa-chart-column text-blue"></i> {timeframe} Visibility &amp;
              Citation Trajectory
            </h3>
            <span className="seo-subtle-hint" style={{ fontSize: '11px' }}>
              Interactive multi-engine telemetry. Click legend items to toggle series.
            </span>
          </div>

          <div className="seo-chart-controls">
            {/* Timeframe Picker */}
            <div className="seo-timeframe-picker" data-testid="timeframe-picker">
              {['7D', '14D', '30D', '90D'].map((tf) => (
                <button
                  key={tf}
                  className={`seo-timeframe-btn ${timeframe === tf ? 'active' : ''}`}
                  onClick={() => setTimeframe(tf)}
                  data-testid={`timeframe-btn-${tf}`}
                >
                  {tf}
                </button>
              ))}
            </div>

            {/* Clickable Legend Toggles */}
            <div className="seo-chart-legend">
              <span
                className={`legend-item toggleable ${!activeSeries.google ? 'inactive' : ''}`}
                onClick={() => toggleSeries('google')}
                title="Toggle Google Clicks"
                data-testid="legend-toggle-google"
              >
                <span className="legend-dot blue"></span> Google Clicks
              </span>
              <span
                className={`legend-item toggleable ${!activeSeries.perplexity ? 'inactive' : ''}`}
                onClick={() => toggleSeries('perplexity')}
                title="Toggle Perplexity Citations"
                data-testid="legend-toggle-perplexity"
              >
                <span className="legend-dot purple"></span> Perplexity
              </span>
              <span
                className={`legend-item toggleable ${!activeSeries.copilot ? 'inactive' : ''}`}
                onClick={() => toggleSeries('copilot')}
                title="Toggle Copilot Mentions"
                data-testid="legend-toggle-copilot"
              >
                <span className="legend-dot green"></span> Copilot
              </span>
              <span
                className={`legend-item toggleable ${!activeSeries.chatgpt ? 'inactive' : ''}`}
                onClick={() => toggleSeries('chatgpt')}
                title="Toggle ChatGPT Citations"
                data-testid="legend-toggle-chatgpt"
              >
                <span className="legend-dot emerald" style={{ background: '#16a34a' }}></span>{' '}
                ChatGPT
              </span>
            </div>
          </div>
        </div>

        {/* Visual Bar Progression with dynamic heights and tooltip popover */}
        <div className="seo-chart-bars-wrap" data-testid="chart-bars-container">
          {visibleTimeSeries.length === 0 ? (
            <div
              className="seo-empty-state"
              data-testid="chart-empty-state"
              style={{ padding: '36px 20px', width: '100%', textAlign: 'center' }}
            >
              <i
                className="fas fa-chart-column"
                style={{ fontSize: '32px', color: '#cbd5e1', marginBottom: '12px' }}
              ></i>
              <h4 style={{ margin: '0 0 6px 0', color: '#0f172a', fontWeight: 600 }}>
                No Visibility Telemetry Yet
              </h4>
              <p
                style={{
                  margin: '0 0 16px 0',
                  color: '#64748b',
                  fontSize: '13px',
                  maxWidth: '440px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                Run your first live answer engine check or load sample telemetry to measure organic
                search clicks and AI citations across Perplexity, ChatGPT, and Google AI Overviews.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="seo-btn seo-btn-primary"
                  onClick={() => setIsScannerOpen(true)}
                  data-testid="btn-empty-run-scanner"
                >
                  <i className="fas fa-satellite-dish"></i> Run Visibility Check
                </button>
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={handleLoadSampleData}
                  data-testid="btn-load-sample-data"
                >
                  <i className="fas fa-database"></i> Load Sample Data
                </button>
              </div>
            </div>
          ) : (
            visibleTimeSeries.map((item, idx) => {
              const googleClicks = Number(item.googleClicks) || 0;
              const perpCitations = Number(item.perplexityCitations) || 0;
              const copilotMentions = Number(item.copilotMentions) || 0;
              const chatgptCitations = Number(item.chatgptCitations) || 0;
              const conversions = Number(item.conversions) || 0;

              const googleBarHeight =
                activeSeries.google && googleClicks > 0
                  ? Math.max(8, Math.round((googleClicks / maxClicks) * 110))
                  : 0;
              const perpBarHeight =
                activeSeries.perplexity && perpCitations > 0
                  ? Math.max(6, Math.round((perpCitations / maxCitations) * 90))
                  : 0;
              const copilotBarHeight =
                activeSeries.copilot && copilotMentions > 0
                  ? Math.max(4, Math.round((copilotMentions / maxCitations) * 75))
                  : 0;
              const chatgptBarHeight =
                activeSeries.chatgpt && chatgptCitations > 0
                  ? Math.max(4, Math.round((chatgptCitations / maxCitations) * 70))
                  : 0;

              return (
                <div
                  key={idx}
                  className="seo-bar-group"
                  onMouseEnter={() => setHoveredPoint(item)}
                  onMouseLeave={() => setHoveredPoint(null)}
                  data-testid={`bar-group-${idx}`}
                >
                  {/* Hover Popover Tooltip */}
                  {hoveredPoint === item && (
                    <div className="seo-bar-tooltip">
                      <strong>{item.date}</strong>
                      {activeSeries.google && (
                        <div>Google: {googleClicks.toLocaleString()} clicks</div>
                      )}
                      {activeSeries.perplexity && <div>Perplexity: {perpCitations} citations</div>}
                      {activeSeries.copilot && <div>Copilot: {copilotMentions} mentions</div>}
                      {activeSeries.chatgpt && <div>ChatGPT: {chatgptCitations} citations</div>}
                      <div style={{ color: '#34d399', marginTop: '2px' }}>
                        Conversions: {conversions} MQLs
                      </div>
                    </div>
                  )}

                  <div className="seo-bars-column">
                    {activeSeries.google && (
                      <div
                        className="seo-bar blue"
                        style={{ height: `${googleBarHeight}px` }}
                        title={`Google Clicks: ${googleClicks}`}
                      ></div>
                    )}
                    {activeSeries.perplexity && (
                      <div
                        className="seo-bar purple"
                        style={{ height: `${perpBarHeight}px` }}
                        title={`Perplexity Citations: ${perpCitations}`}
                      ></div>
                    )}
                    {activeSeries.copilot && (
                      <div
                        className="seo-bar green"
                        style={{ height: `${copilotBarHeight}px` }}
                        title={`Copilot Mentions: ${copilotMentions}`}
                      ></div>
                    )}
                    {activeSeries.chatgpt && (
                      <div
                        className="seo-bar"
                        style={{ height: `${chatgptBarHeight}px`, background: '#16a34a' }}
                        title={`ChatGPT Citations: ${chatgptCitations}`}
                      ></div>
                    )}
                  </div>
                  <span className="seo-bar-label">{item.date}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RECENT CITATIONS FEED */}
      <div className="seo-citations-feed-card" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 className="seo-panel-title">
              <i className="fas fa-quote-right text-purple"></i> Live AI Engine Citations Feed
            </h3>
            <p className="seo-subtle-hint">
              Real-time detected prompt occurrences where generative models cited your workspace
              content as a grounding source.
            </p>
          </div>
          <span className="seo-counter-pill" data-testid="citation-count-badge">
            {filteredCitations.length} Citations Found
          </span>
        </div>

        {/* Toolbar: Search & Engine Filters */}
        <div className="seo-citations-toolbar">
          <div className="seo-citations-filter-row">
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
              Filter Engine:
            </span>
            {[
              { id: 'all', label: 'All Engines' },
              { id: 'Perplexity AI', label: 'Perplexity AI' },
              { id: 'Google AI Overview', label: 'Google AI' },
              { id: 'ChatGPT Search', label: 'ChatGPT' },
              { id: 'Microsoft Copilot', label: 'Copilot' },
            ].map((eng) => (
              <button
                key={eng.id}
                className={`seo-filter-chip ${selectedEngine === eng.id ? 'active' : ''}`}
                onClick={() => setSelectedEngine(eng.id)}
                data-testid={`filter-chip-${eng.id}`}
              >
                {eng.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              className="seo-search-input"
              style={{ width: '220px', padding: '6px 12px', fontSize: '12px' }}
              placeholder="Search prompt, URL, or snippet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="citations-search-input"
            />
            <select
              className="seo-search-input"
              style={{ padding: '6px 10px', fontSize: '12px' }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="citations-sort-select"
            >
              <option value="newest">Sort: Newest First</option>
              <option value="conversions">Sort: Most Attributed Leads</option>
              <option value="engine">Sort: Engine Name</option>
            </select>
          </div>
        </div>

        {/* Citations List */}
        <div className="seo-citations-list" data-testid="citations-list">
          {filteredCitations.length === 0 ? (
            <div className="seo-empty-box" style={{ padding: '40px 20px', textAlign: 'center' }}>
              <i
                className={
                  rawRecentCitations.length === 0 ? 'fas fa-quote-right' : 'fas fa-magnifying-glass'
                }
                style={{ fontSize: '28px', color: '#94a3b8' }}
              ></i>
              <p style={{ marginTop: '12px', color: '#475569', fontWeight: 500 }}>
                {rawRecentCitations.length === 0
                  ? 'No citations recorded yet for this workspace.'
                  : 'No citations match the current filter or search criteria.'}
              </p>
              {rawRecentCitations.length === 0 ? (
                <div
                  style={{
                    marginTop: '12px',
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center',
                  }}
                >
                  <button
                    type="button"
                    className="seo-btn seo-btn-primary"
                    onClick={() => setIsScannerOpen(true)}
                  >
                    <i className="fas fa-satellite-dish"></i> Check Visibility
                  </button>
                  <button
                    type="button"
                    className="seo-btn seo-btn-secondary"
                    onClick={() => setIsAddModalOpen(true)}
                  >
                    <i className="fas fa-plus"></i> Log Citation
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  style={{ marginTop: '8px' }}
                  onClick={() => {
                    setSelectedEngine('all');
                    setSearchQuery('');
                  }}
                >
                  Reset Filters
                </button>
              )}
            </div>
          ) : (
            filteredCitations.map((cit) => {
              const engineClass = (cit.engine || 'unknown').toLowerCase().replace(/\s+/g, '-');

              return (
                <div
                  key={cit.id}
                  className="seo-citation-card"
                  data-testid={`citation-card-${cit.id}`}
                >
                  <div className="seo-citation-top">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={`seo-engine-badge ${engineClass}`}>
                        <i className="fas fa-robot"></i> {cit.engine}
                      </span>
                      {cit.confidenceScore && (
                        <span
                          className="seo-pill-tag"
                          style={{ fontSize: '10px', padding: '1px 6px' }}
                          title="Semantic grounding confidence"
                        >
                          {cit.confidenceScore}% Grounded
                        </span>
                      )}
                    </div>
                    <span className="seo-citation-time">{cit.timestamp}</span>
                  </div>

                  <div className="seo-citation-query-row">
                    <span className="seo-citation-prompt-tag">User Prompt:</span>
                    <strong>"{cit.query}"</strong>
                  </div>

                  <blockquote className="seo-citation-snippet">
                    <i className="fas fa-quote-left text-purple"></i>
                    <span>{cit.snippetText}</span>
                  </blockquote>

                  <div className="seo-citation-footer">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>Cited Source:</span>
                      <code>{cit.citedPage}</code>
                    </div>

                    <div className="seo-citation-actions">
                      <span
                        className="seo-attribution-badge"
                        title="Leads originating from this AI grounding"
                      >
                        <i className="fas fa-bullseye"></i> {cit.attributedLeads || 0} Attributed
                        Leads
                      </span>

                      <button
                        className="seo-btn-xs"
                        onClick={() => handleCopySnippet(cit)}
                        title="Copy citation snippet"
                        data-testid={`btn-copy-snippet-${cit.id}`}
                      >
                        <i
                          className={
                            copiedId === cit.id ? 'fas fa-check text-emerald' : 'fas fa-copy'
                          }
                        ></i>
                        {copiedId === cit.id ? 'Copied' : 'Copy'}
                      </button>

                      <button
                        className="seo-btn-xs"
                        onClick={() => setActiveAttributionJourney(cit)}
                        title="View conversion attribution journey"
                        data-testid={`btn-attribution-${cit.id}`}
                      >
                        <i className="fas fa-diagram-project"></i> Attribution
                      </button>

                      <button
                        className="seo-btn-xs danger"
                        onClick={() => handleDeleteCitation(cit.id)}
                        title="Dismiss citation record"
                        data-testid={`btn-delete-${cit.id}`}
                      >
                        <i className="fas fa-trash-can"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── MODAL 1: LIVE VISIBILITY & CITATION CHECK SCANNER ── */}
      {isScannerOpen && (
        <div
          className="seo-modal-overlay"
          data-testid="scanner-modal"
          onClick={() => !isScanning && setIsScannerOpen(false)}
        >
          <div
            className="seo-modal-dialog"
            style={{ maxWidth: '580px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="seo-modal-header">
              <h3 className="seo-modal-title">
                <i className="fas fa-satellite-dish text-blue"></i> Run Live Answer Engine
                Visibility Check
              </h3>
              <button
                className="seo-modal-close-btn"
                onClick={() => setIsScannerOpen(false)}
                disabled={isScanning}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleRunLiveScan}>
              <div className="seo-modal-body">
                <p className="seo-subtle-hint" style={{ marginBottom: '16px' }}>
                  Evaluate if AI answer engines currently cite your content for high-intent search
                  queries.
                </p>

                {scanError && (
                  <div className="seo-alert-box danger" style={{ marginBottom: '12px' }}>
                    <i className="fas fa-circle-exclamation"></i> {scanError}
                  </div>
                )}

                <div className="seo-form-group">
                  <label className="seo-form-label">Target User Prompt / Query *</label>
                  <input
                    type="text"
                    className="seo-form-input"
                    placeholder="e.g. What are the best cold outreach cadence tools?"
                    value={scanQuery}
                    onChange={(e) => setScanQuery(e.target.value)}
                    required
                    data-testid="scan-query-input"
                  />
                  {topicClusters.length > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#64748b' }}>
                      <span>Quick Pick: </span>
                      {topicClusters.slice(0, 3).map((tc) => (
                        <button
                          key={tc.id}
                          type="button"
                          className="seo-text-btn"
                          style={{ marginRight: '6px', textDecoration: 'underline' }}
                          onClick={() => setScanQuery(tc.topic)}
                        >
                          {tc.topic}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="seo-form-group">
                  <label className="seo-form-label">Target Answer Engine</label>
                  <select
                    className="seo-form-input"
                    value={scanEngine}
                    onChange={(e) => setScanEngine(e.target.value)}
                    data-testid="scan-engine-select"
                  >
                    <option value="Perplexity AI">Perplexity AI</option>
                    <option value="Google AI Overview">Google AI Overview</option>
                    <option value="ChatGPT Search">ChatGPT Search</option>
                    <option value="Microsoft Copilot">Microsoft Copilot</option>
                    <option value="Claude">Claude</option>
                  </select>
                </div>

                <div className="seo-form-group">
                  <label className="seo-form-label">Target Grounding Page</label>
                  <input
                    type="text"
                    className="seo-form-input"
                    value={scanTargetPage}
                    onChange={(e) => setScanTargetPage(e.target.value)}
                    placeholder="/blog/your-guide-slug"
                    data-testid="scan-page-input"
                  />
                  {briefs.length > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#64748b' }}>
                      <span>Active Briefs: </span>
                      {briefs.slice(0, 2).map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          className="seo-text-btn"
                          style={{ marginRight: '6px', textDecoration: 'underline' }}
                          onClick={() => setScanTargetPage(b.targetSlug || '/blog/aieo-guide')}
                        >
                          {b.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsScannerOpen(false)}
                  disabled={isScanning}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="seo-btn seo-btn-primary"
                  disabled={isScanning || !scanQuery.trim()}
                  data-testid="btn-submit-scan"
                >
                  {isScanning ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Scanning Grounding...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magnifying-glass-location"></i> Scan Grounding
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL 2: LOG MANUAL CITATION ── */}
      {isAddModalOpen && (
        <div
          className="seo-modal-overlay"
          data-testid="add-citation-modal"
          onClick={() => setIsAddModalOpen(false)}
        >
          <div
            className="seo-modal-dialog"
            style={{ maxWidth: '540px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="seo-modal-header">
              <h3 className="seo-modal-title">
                <i className="fas fa-plus-circle text-purple"></i> Log Manual Citation
              </h3>
              <button className="seo-modal-close-btn" onClick={() => setIsAddModalOpen(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleAddManualCitation}>
              <div className="seo-modal-body">
                <div className="seo-form-group">
                  <label className="seo-form-label">Answer Engine *</label>
                  <select
                    className="seo-form-input"
                    value={manualForm.engine}
                    onChange={(e) => setManualForm({ ...manualForm, engine: e.target.value })}
                  >
                    <option value="Perplexity AI">Perplexity AI</option>
                    <option value="Google AI Overview">Google AI Overview</option>
                    <option value="ChatGPT Search">ChatGPT Search</option>
                    <option value="Microsoft Copilot">Microsoft Copilot</option>
                    <option value="Claude">Claude</option>
                  </select>
                </div>

                <div className="seo-form-group">
                  <label className="seo-form-label">Observed User Prompt / Query *</label>
                  <input
                    type="text"
                    className="seo-form-input"
                    placeholder="e.g. Best sales prospecting software for APAC"
                    value={manualForm.query}
                    onChange={(e) => setManualForm({ ...manualForm, query: e.target.value })}
                    required
                    data-testid="manual-query-input"
                  />
                </div>

                <div className="seo-form-group">
                  <label className="seo-form-label">Cited Workspace Page *</label>
                  <input
                    type="text"
                    className="seo-form-input"
                    placeholder="/blog/your-page-slug"
                    value={manualForm.citedPage}
                    onChange={(e) => setManualForm({ ...manualForm, citedPage: e.target.value })}
                    required
                  />
                </div>

                <div className="seo-form-group">
                  <label className="seo-form-label">Extracted Snippet Text</label>
                  <textarea
                    className="seo-form-input"
                    rows={3}
                    placeholder="Enter the quotation or citation extract returned by the answer engine..."
                    value={manualForm.snippetText}
                    onChange={(e) => setManualForm({ ...manualForm, snippetText: e.target.value })}
                  ></textarea>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="seo-form-group">
                    <label className="seo-form-label">Grounding Confidence (%)</label>
                    <input
                      type="number"
                      min={50}
                      max={100}
                      className="seo-form-input"
                      value={manualForm.confidenceScore}
                      onChange={(e) =>
                        setManualForm({ ...manualForm, confidenceScore: e.target.value })
                      }
                    />
                  </div>
                  <div className="seo-form-group">
                    <label className="seo-form-label">Attributed Leads</label>
                    <input
                      type="number"
                      min={0}
                      className="seo-form-input"
                      value={manualForm.attributedLeads}
                      onChange={(e) =>
                        setManualForm({ ...manualForm, attributedLeads: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="seo-btn seo-btn-primary"
                  data-testid="btn-save-manual-citation"
                >
                  Save Citation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL 3: ATTRIBUTION JOURNEY MODAL ── */}
      {activeAttributionJourney && (
        <div
          className="seo-modal-overlay"
          data-testid="attribution-journey-modal"
          onClick={() => setActiveAttributionJourney(null)}
        >
          <div
            className="seo-modal-dialog"
            style={{ maxWidth: '600px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="seo-modal-header">
              <h3 className="seo-modal-title">
                <i className="fas fa-diagram-project text-emerald"></i> Lead Attribution Journey
              </h3>
              <button
                className="seo-modal-close-btn"
                onClick={() => setActiveAttributionJourney(null)}
              >
                &times;
              </button>
            </div>

            <div className="seo-modal-body">
              <div style={{ marginBottom: '16px' }}>
                <span className="seo-subtle-hint">Source Citation Grounding</span>
                <h4 style={{ margin: '4px 0', fontSize: '15px', color: '#0f172a' }}>
                  "{activeAttributionJourney.query}"
                </h4>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <span className="seo-engine-badge perplexity-ai">
                    {activeAttributionJourney.engine}
                  </span>
                  <span className="seo-pill-tag">
                    {activeAttributionJourney.confidenceScore || 92}% Grounding Confidence
                  </span>
                </div>
              </div>

              {/* Attribution Flow Timeline */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  background: '#f8fafc',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#eff6ff',
                      color: '#2563eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    1
                  </div>
                  <div>
                    <strong style={{ fontSize: '13px' }}>AI Answer Engine Query</strong>
                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                      User asked: "{activeAttributionJourney.query}" on{' '}
                      {activeAttributionJourney.engine}.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#faf5ff',
                      color: '#7c3aed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    2
                  </div>
                  <div>
                    <strong style={{ fontSize: '13px' }}>Grounded Source Citation</strong>
                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                      Generative model extracted citation from{' '}
                      <code>{activeAttributionJourney.citedPage}</code>.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#ecfdf5',
                      color: '#059669',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    3
                  </div>
                  <div>
                    <strong style={{ fontSize: '13px' }}>
                      Lead Capture &amp; Pipeline Conversion
                    </strong>
                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                      {activeAttributionJourney.attributedLeads || 1} MQL prospect(s) clicked the
                      cited source link, completed lead capture, and routed directly to CRM
                      pipeline.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="seo-modal-footer">
              <button
                className="seo-btn seo-btn-primary"
                onClick={() => setActiveAttributionJourney(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL 4: ALERTS CONFIGURATION MODAL (FR-M11-008) ── */}
      {isAlertsModalOpen && (
        <div
          className="seo-modal-overlay"
          data-testid="alerts-modal"
          onClick={() => setIsAlertsModalOpen(false)}
        >
          <div
            className="seo-modal-dialog"
            style={{ maxWidth: '520px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="seo-modal-header">
              <h3 className="seo-modal-title">
                <i className="fas fa-bell text-amber"></i> Visibility &amp; Citation Alerts
                (FR-M11-008)
              </h3>
              <button className="seo-modal-close-btn" onClick={() => setIsAlertsModalOpen(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveAlerts}>
              <div className="seo-modal-body">
                <p className="seo-subtle-hint" style={{ marginBottom: '16px' }}>
                  Configure automated alerts when visibility or AI citation grounding volume changes
                  materially.
                </p>

                <div className="seo-form-group">
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={alertsForm.decayAlert}
                      onChange={(e) =>
                        setAlertsForm({ ...alertsForm, decayAlert: e.target.checked })
                      }
                      data-testid="alert-decay-checkbox"
                    />
                    <div>
                      <strong style={{ fontSize: '13px' }}>Citation Drop / Decay Alert</strong>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        Notify when 7-day citation frequency declines.
                      </div>
                    </div>
                  </label>
                </div>

                {alertsForm.decayAlert && (
                  <div className="seo-form-group" style={{ marginLeft: '26px' }}>
                    <label className="seo-form-label">Drop Threshold Percentage (%)</label>
                    <input
                      type="number"
                      min={5}
                      max={80}
                      className="seo-form-input"
                      value={alertsForm.thresholdPercent}
                      onChange={(e) =>
                        setAlertsForm({
                          ...alertsForm,
                          thresholdPercent: Number(e.target.value) || 20,
                        })
                      }
                      data-testid="alert-threshold-input"
                    />
                  </div>
                )}

                <div className="seo-form-group">
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={alertsForm.competitorAlert}
                      onChange={(e) =>
                        setAlertsForm({ ...alertsForm, competitorAlert: e.target.checked })
                      }
                    />
                    <div>
                      <strong style={{ fontSize: '13px' }}>Competitor Displacement Alert</strong>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        Trigger alert if a tracked competitor out-ranks brand citations in AI answer
                        engines.
                      </div>
                    </div>
                  </label>
                </div>

                <div className="seo-form-group">
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={alertsForm.emailNotifications}
                      onChange={(e) =>
                        setAlertsForm({ ...alertsForm, emailNotifications: e.target.checked })
                      }
                    />
                    <div>
                      <strong style={{ fontSize: '13px' }}>Email &amp; In-App Notifications</strong>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        Dispatch alerts immediately to workspace marketing admins.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsAlertsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="seo-btn seo-btn-primary"
                  data-testid="btn-save-alerts"
                >
                  Save Alert Settings
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
